import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '../../lib/apiClient';
import { planClass } from './planBadge';
import { describeOverrideEvent, limitFields, matchingAttribution, type OverrideAttribution, type OverrideField } from './overrideContext';
import styles from './Admin.module.css';
import { formatWhen } from '../../lib/time';

/**
 * Admin → Overrides.
 *
 * Also, since TOADO-493 and TOADO-496, each workspace's recording switch and
 * its own recording limits (POST /admin/companies/:id/feature-access and
 * /recording-limits, audited the same way), and since TOADO-655 its widget
 * allowances (visitor minutes and widget reports) through the same route.
 *
 * Two forms on one page: set a per-company ticket quota override
 * (number + reason) and manually flip a company's plan (without
 * Stripe). Server side: POST /admin/companies/:id/quota-override and
 * POST /admin/companies/:id/plan, both audited as 'admin.*'. The
 * existing /admin/companies list is the source of truth for the
 * current state, so this page reuses that query and refetches on
 * mutation success.
 *
 * Accounts and recent override events use separate horizontal tabs.
 */
interface AdminCompanyRow {
  id: string;
  name: string;
  plan: 'free' | 'personal' | 'team' | 'business' | 'enterprise';
  ticketQuotaOverride: number | null;
  quotaOverrideReason: string | null;
  planOverrideAt: string | null;
  featureAccess: 'default' | 'on' | 'off';
  recordingMinutesOverride: number | null;
  transcriptionMinutesOverride: number | null;
  storageGbOverride: number | null;
  visitorMinutesOverride: number | null;
  widgetReportsOverride: number | null;
  overrideAttribution?: Partial<Record<OverrideField, OverrideAttribution>>;
}

interface AdminCompaniesResponse {
  companies: AdminCompanyRow[];
  featureAudience: 'invited' | 'everyone';
}

interface OverrideEvent {
  id: string;
  companyId: string;
  actorId: string;
  actorName?: string | null;
  companyName?: string | null;
  action: string;
  metadata: Record<string, unknown> | null;
  occurredAt: string | null;
}

interface OverridesHistoryResponse {
  events: OverrideEvent[];
}

const overrideTabs = [
  { id: 'accounts', label: 'Accounts' },
  { id: 'events', label: 'Override events' },
] as const;

export function OverridesPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<typeof overrideTabs[number]['id']>('accounts');
  const tabButtons = useRef<Partial<Record<typeof overrideTabs[number]['id'], HTMLButtonElement | null>>>({});
  const [historyCompany, setHistoryCompany] = useState('');
  const [historyCompanyName, setHistoryCompanyName] = useState('');
  const { data, isLoading, error: companiesError } = useQuery({
    queryKey: ['admin', 'companies'],
    queryFn: () => get<AdminCompaniesResponse>('/admin/companies'),
    staleTime: 30_000,
  });
  const { data: history, error: historyError } = useQuery({
    queryKey: ['admin', 'overrides', 'history', historyCompany],
    queryFn: () => get<OverridesHistoryResponse>(`/admin/overrides/history${historyCompany ? `?companyId=${encodeURIComponent(historyCompany)}` : ''}`),
    staleTime: 30_000,
  });

  const setQuota = useMutation({
    mutationFn: async (input: { companyId: string; ticketCap: number | null; reason: string }) => {
      return post(`/admin/companies/${input.companyId}/quota-override`, {
        ticketCap: input.ticketCap,
        reason: input.reason,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'companies'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'overrides', 'history'] });
    },
  });

  const setAccess = useMutation({
    mutationFn: async (input: { companyId: string; access: AdminCompanyRow['featureAccess']; reason: string }) => {
      return post(`/admin/companies/${input.companyId}/feature-access`, { access: input.access, reason: input.reason });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'companies'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'overrides', 'history'] });
    },
  });

  const setLimits = useMutation({
    mutationFn: async (input: { companyId: string; limits: RecordingLimitsInput; reason: string }) => {
      return post(`/admin/companies/${input.companyId}/recording-limits`, { ...input.limits, reason: input.reason });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'companies'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'overrides', 'history'] });
    },
  });

  const setPlan = useMutation({
    mutationFn: async (input: { companyId: string; plan: AdminCompanyRow['plan']; reason: string }) => {
      return post(`/admin/companies/${input.companyId}/plan`, {
        plan: input.plan,
        reason: input.reason,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'companies'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'overrides', 'history'] });
    },
  });

  return (
    <>
      <h1 className={styles.h1}>Overrides</h1>
      <p className={styles.sub}>
        Manually set a per-account monthly ticket cap, or flip an account's
        plan tier without routing through Stripe. Use for beta-tester comps,
        friends, or one-off raised limits. Changes use the existing
        audit log; the Override events tab shows the last 50 events. Missing
        audit attribution is shown explicitly.
      </p>
      <div className={styles.innerTabs} role="tablist" aria-label="Overrides views">
        {overrideTabs.map((tab, index) => (
          <button
            key={tab.id}
            ref={(node) => { tabButtons.current[tab.id] = node; }}
            type="button"
            role="tab"
            id={`overrides-tab-${tab.id}`}
            aria-controls={`overrides-panel-${tab.id}`}
            aria-selected={activeTab === tab.id}
            tabIndex={activeTab === tab.id ? 0 : -1}
            className={`${styles.innerTab} ${activeTab === tab.id ? styles.innerTabActive : ''}`}
            onClick={() => setActiveTab(tab.id)}
            onKeyDown={(event) => {
              if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
              const next = event.key === 'Home' ? 0 : event.key === 'End' ? overrideTabs.length - 1
                : event.key === 'ArrowRight' ? (index + 1) % overrideTabs.length
                : event.key === 'ArrowLeft' ? (index - 1 + overrideTabs.length) % overrideTabs.length : undefined;
              if (next === undefined) return;
              const target = overrideTabs[next];
              if (!target) return;
              event.preventDefault();
              setActiveTab(target.id);
              tabButtons.current[target.id]?.focus();
            }}
          >{tab.label}</button>
        ))}
      </div>
      {/* Keep editors mounted so switching views preserves unsaved reasons. */}
      <section role="tabpanel" id="overrides-panel-accounts" aria-labelledby="overrides-tab-accounts" hidden={activeTab !== 'accounts'} tabIndex={0}>
        {data && (
          <p className={styles.sub}>
            Recording and pins: <strong>Default</strong> follows the server setting, which is{' '}
            <strong>{data.featureAudience === 'everyone'
              ? 'every workspace not switched off'
              : 'staff and the workspaces switched on'}</strong>.
            On opens them for a workspace whatever that says; Off closes them even when everyone has them.
          </p>
        )}

        {[setQuota.error, setPlan.error, setAccess.error, setLimits.error].filter(Boolean).map((error, i) => (
          <p role="alert" key={i}>{error instanceof Error ? error.message : 'Could not save the override. Your entries are kept. Please retry.'}</p>
        ))}
        <div className={styles.section}>
          {companiesError ? <p role="alert">Could not load accounts. Refresh to retry.</p> : isLoading || !data ? (
            <p className={styles.muted}>Loading…</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Plan</th>
                  <th>Current overrides and reasons</th>
                  <th>Recording access</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.companies.map((c) => (
                  <OverrideRow
                    key={c.id}
                    company={c}
                    setQuota={(ticketCap, reason) => setQuota.mutate({ companyId: c.id, ticketCap, reason })}
                    setPlan={(plan, reason) => setPlan.mutate({ companyId: c.id, plan, reason })}
                    setAccess={(access, reason) => setAccess.mutate({ companyId: c.id, access, reason })}
                    setLimits={(limits, reason) => setLimits.mutate({ companyId: c.id, limits, reason })}
                    showHistory={() => {
                      setHistoryCompany(c.id);
                      setHistoryCompanyName(c.name);
                      setActiveTab('events');
                      tabButtons.current.events?.focus();
                    }}
                    busy={setQuota.isPending || setPlan.isPending || setAccess.isPending || setLimits.isPending}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
      <section role="tabpanel" id="overrides-panel-events" aria-labelledby="overrides-tab-events" hidden={activeTab !== 'events'} tabIndex={0}>
        <h2 id="override-history" style={{ fontSize: 18, marginBottom: 8 }}>Recent override events</h2>
        <label className={styles.historyFilter}>
          Filter history by account
          <select value={historyCompany} onChange={(event) => {
            setHistoryCompany(event.target.value);
            setHistoryCompanyName(event.target.selectedOptions[0]?.textContent ?? '');
          }} className={styles.selectInput}>
            <option value="">All accounts</option>
            {Array.from(new Map([
              ...(historyCompany ? [[historyCompany, historyCompanyName || historyCompany] as const] : []),
              ...(data?.companies ?? []).map(company => [company.id, company.name] as const),
              ...(history?.events ?? []).map(event => [event.companyId, event.companyName ?? event.companyId] as const),
            ]).entries()).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </label>
        <div className={styles.section} role="region" aria-label="Recent override events">
          {historyError ? <p role="alert">Could not load override history. Refresh to retry.</p> : !history ? (
            <p className={styles.muted}>Loading…</p>
          ) : history.events.length === 0 ? (
            <p className={styles.muted}>No override events yet.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Action</th>
                  <th>Account</th>
                  <th>Actor</th>
                  <th>Reason</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {history.events.map((e) => (
                  <tr key={e.id}>
                    <td className={styles.muted}>{formatDate(e.occurredAt)}</td>
                    <td>{e.action.replace(/^admin\./, '').replaceAll('_', ' ')}</td>
                    <td><span>{e.companyName ?? data?.companies.find(company => company.id === e.companyId)?.name ?? 'Unknown account'}</span><small className={styles.overrideMeta}>{e.companyId}</small></td>
                    <td title={e.actorId}>{e.actorName || e.actorId}</td>
                    <td>{typeof e.metadata?.reason === 'string' ? e.metadata.reason : 'Reason unavailable'}</td>
                    <td className={styles.overrideDetails}>{describeOverrideEvent(e.action, e.metadata)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </>
  );
}

type RecordingLimitsInput = {
  recordingMinutes: number | null;
  transcriptionMinutes: number | null;
  storageGb: number | null;
  visitorMinutes: number | null;
  /** Left out on Free, where reports follow the ticket cap: the server keeps what is stored. */
  widgetReports?: number | null;
};

function OverrideRow(props: {
  company: AdminCompanyRow;
  setQuota: (ticketCap: number | null, reason: string) => void;
  setPlan: (plan: AdminCompanyRow['plan'], reason: string) => void;
  setAccess: (access: AdminCompanyRow['featureAccess'], reason: string) => void;
  setLimits: (limits: RecordingLimitsInput, reason: string) => void;
  showHistory: () => void;
  busy: boolean;
}) {
  const { company: c, setQuota, setPlan, setAccess, setLimits, busy } = props;
  const [access, setAccessLocal] = useState<AdminCompanyRow['featureAccess']>(c.featureAccess);
  const currentReason = (field: OverrideField, value: unknown) => matchingAttribution(c, field, value)?.reason ?? '';
  const latestLimitReason = () => limitFields.map(([field]) => matchingAttribution(c, field, c[field]))
    .filter((entry): entry is OverrideAttribution => !!entry)
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))[0]?.reason ?? '';
  const [accessReason, setAccessReason] = useState(() => currentReason('featureAccess', c.featureAccess));
  const text = (value: number | null) => (value == null ? '' : String(value));
  const [recordingMinutes, setRecordingMinutes] = useState(text(c.recordingMinutesOverride));
  const [transcriptionMinutes, setTranscriptionMinutes] = useState(text(c.transcriptionMinutesOverride));
  const [storageGb, setStorageGb] = useState(text(c.storageGbOverride));
  const [visitorMinutes, setVisitorMinutes] = useState(text(c.visitorMinutesOverride));
  const [widgetReports, setWidgetReports] = useState(text(c.widgetReportsOverride));
  const [limitsReason, setLimitsReason] = useState(latestLimitReason);
  const count = (value: string) => (value.trim() === '' ? null : Number(value));
  const [editing, setEditing] = useState(false);
  const [cap, setCap] = useState<string>(c.ticketQuotaOverride == null ? '' : String(c.ticketQuotaOverride));
  const [reason, setReason] = useState<string>(c.quotaOverrideReason ?? '');
  const [plan, setPlanLocal] = useState<AdminCompanyRow['plan']>(c.plan);
  const [planReason, setPlanReason] = useState(() => currentReason('plan', c.plan));
  const hasOverrides = c.planOverrideAt || c.ticketQuotaOverride != null || limitFields.some(([field]) => c[field] != null);
  const quotaEntry = matchingAttribution(c, 'ticketQuotaOverride', c.ticketQuotaOverride);
  // The reason column is authoritative even when the best-effort audit failed
  // after a same-value update. Do not attribute its new reason to an old actor.
  const quotaAttribution = quotaEntry?.reason === c.quotaOverrideReason ? quotaEntry : undefined;

  function resetDraft() {
    setAccessLocal(c.featureAccess);
    setAccessReason(currentReason('featureAccess', c.featureAccess));
    setRecordingMinutes(text(c.recordingMinutesOverride));
    setTranscriptionMinutes(text(c.transcriptionMinutesOverride));
    setStorageGb(text(c.storageGbOverride));
    setVisitorMinutes(text(c.visitorMinutesOverride));
    setWidgetReports(text(c.widgetReportsOverride));
    setLimitsReason(latestLimitReason());
    setCap(text(c.ticketQuotaOverride));
    setReason(c.quotaOverrideReason ?? '');
    setPlanLocal(c.plan);
    setPlanReason(currentReason('plan', c.plan));
  }

  return (
    <>
      <tr>
        <td>
          <div className={styles.userCell}>
            <span className={styles.userName}>{c.name}</span>
            <span className={styles.userEmail}>{c.id}</span>
          </div>
        </td>
        <td>
          <span className={`${styles.planBadge} ${planClass(c.plan)}`}>{c.plan}</span>
          {c.planOverrideAt && (
            <span className={styles.overrideBadge} title={`Manual change recorded on ${formatDate(c.planOverrideAt)}. Stripe may reconcile the plan after seven days.`}>
              manual history
            </span>
          )}
        </td>
        <td className={styles.overrideDetails}>
          {c.planOverrideAt && <OverrideNote label={`Current plan: ${c.plan}`} context="Latest matching manual plan change" entry={matchingAttribution(c, 'plan', c.plan)} />}
          {c.ticketQuotaOverride != null && <OverrideNote label={`Monthly ticket cap: ${c.ticketQuotaOverride.toLocaleString()}`} entry={quotaAttribution} fallbackReason={c.quotaOverrideReason} />}
          {limitFields.map(([field, label]) => {
            const value = c[field];
            return value == null ? null : <OverrideNote key={field} label={`${label}: ${value.toLocaleString()}`} entry={matchingAttribution(c, field, value)} />;
          })}
          {!hasOverrides && <span className={styles.muted}>No plan or limit overrides</span>}
        </td>
        <td className={styles.muted}>
          {c.featureAccess === 'default' ? 'Default' : c.featureAccess === 'on' ? 'On' : 'Off'}
          {c.featureAccess !== 'default' && <OverrideNote label="Access override" entry={matchingAttribution(c, 'featureAccess', c.featureAccess)} />}
        </td>
        <td>
          <button
            type="button"
            className={styles.expandBtn}
            aria-label={`${editing ? 'Cancel editing' : 'Edit'} ${c.name}`}
            onClick={() => { if (!editing) resetDraft(); setEditing((v) => !v); }}
          >
            {editing ? 'Cancel' : 'Edit'}
          </button>
          <button type="button" className={`${styles.linkChip} ${styles.overrideHistoryLink}`} onClick={props.showHistory} aria-label={`History for ${c.name}`}>History</button>
        </td>
      </tr>
      {editing && (
        <tr className={styles.expandRow}>
          <td colSpan={5}>
            <p className={styles.overrideMeta}>
              Plan, access and limit reasons start with the latest matching audit reason.
              Review the reason for this change before saving. The ticket cap uses its saved reason.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24 }}>
              {/* Recording and pins for this workspace (TOADO-493) */}
              <div>
                <h3 style={{ marginTop: 0, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-tertiary)' }}>
                  Recording and pins
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <select value={access} onChange={(e) => setAccessLocal(e.target.value as AdminCompanyRow['featureAccess'])} style={inputStyle}>
                    <option value="default">Default (follow the server setting)</option>
                    <option value="on">On</option>
                    <option value="off">Off</option>
                  </select>
                  <input
                    type="text"
                    value={accessReason}
                    aria-label="Recording access reason"
                    onChange={(e) => setAccessReason(e.target.value)}
                    placeholder="Reason (required)"
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    className={styles.expandBtn}
                    disabled={busy || access === c.featureAccess || accessReason.trim() === ''}
                    onClick={() => setAccess(access, accessReason.trim())}
                  >
                    Apply
                  </button>
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 8 }}>
                  A walkthrough already running when this is switched off may finish.
                </p>
              </div>

              {/* This workspace's own recording limits (TOADO-496) */}
              <div>
                <h3 style={{ marginTop: 0, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-tertiary)' }}>
                  Recording and widget limits
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input type="number" min={0} value={recordingMinutes} onChange={(e) => setRecordingMinutes(e.target.value)} placeholder="Recording minutes / month (plan)" aria-label="Recording minutes a month" style={inputStyle} />
                  <input type="number" min={0} value={transcriptionMinutes} onChange={(e) => setTranscriptionMinutes(e.target.value)} placeholder="Transcribed minutes / month (plan)" aria-label="Transcribed minutes a month" style={inputStyle} />
                  <input type="number" min={0} value={storageGb} onChange={(e) => setStorageGb(e.target.value)} placeholder="Storage GB (plan)" aria-label="Storage GB" style={inputStyle} />
                  <input type="number" min={0} value={visitorMinutes} onChange={(e) => setVisitorMinutes(e.target.value)} placeholder="Visitor minutes / month (plan)" aria-label="Visitor minutes a month" style={inputStyle} />
                  {/* Free's widget reports are its ticket cap, so the server refuses a new figure there; the form leaves the field out. */}
                  <input
                    type="number"
                    min={0}
                    value={widgetReports}
                    onChange={(e) => setWidgetReports(e.target.value)}
                    disabled={c.plan === 'free'}
                    placeholder={c.plan === 'free' ? 'Widget reports follow the ticket cap on Free' : 'Widget reports / month (plan)'}
                    aria-label="Widget reports a month"
                    style={inputStyle}
                  />
                  <input
                    type="text"
                    value={limitsReason}
                    aria-label="Recording and widget limits reason"
                    onChange={(e) => setLimitsReason(e.target.value)}
                    placeholder="Reason (required)"
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    className={styles.expandBtn}
                    disabled={busy || limitsReason.trim() === ''}
                    onClick={() => {
                      setLimits({
                        recordingMinutes: count(recordingMinutes),
                        transcriptionMinutes: count(transcriptionMinutes),
                        storageGb: count(storageGb),
                        visitorMinutes: count(visitorMinutes),
                        ...(c.plan === 'free' ? {} : { widgetReports: count(widgetReports) }),
                      }, limitsReason.trim());
                    }}
                  >
                    Save limits
                  </button>
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 8 }}>
                  For the whole workspace, not per seat. Empty follows the plan. A widget tier
                  still adds its own visitor minutes and reports on top, and on Free the ticket
                  cap holds widget reports instead.
                </p>
              </div>

              {/* Quota override form */}
              <div>
                <h3 style={{ marginTop: 0, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-tertiary)' }}>
                  Set monthly ticket cap
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input
                    type="number"
                    min={0}
                    value={cap}
                    onChange={(e) => setCap(e.target.value)}
                    placeholder="e.g. 100"
                    style={inputStyle}
                  />
                  <input
                    type="text"
                    value={reason}
                    aria-label="Ticket cap reason"
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Reason (required when setting)"
                    style={inputStyle}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className={styles.expandBtn}
                      disabled={busy || cap.trim() === '' || reason.trim() === ''}
                      onClick={() => setQuota(parseInt(cap, 10), reason.trim())}
                    >
                      Set cap
                    </button>
                    <button
                      type="button"
                      className={styles.expandBtn}
                      disabled={busy || c.ticketQuotaOverride == null}
                      onClick={() => setQuota(null, '')}
                    >
                      Clear cap
                    </button>
                  </div>
                </div>
              </div>

              {/* Plan flip form */}
              <div>
                <h3 style={{ marginTop: 0, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-tertiary)' }}>
                  Flip plan (no Stripe)
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <select value={plan} onChange={(e) => setPlanLocal(e.target.value as AdminCompanyRow['plan'])} style={inputStyle}>
                    <option value="free">free</option>
                    <option value="personal">personal</option>
                    <option value="team">team</option>
                    <option value="business">business</option>
                    <option value="enterprise">enterprise</option>
                  </select>
                  <input
                    type="text"
                    value={planReason}
                    aria-label="Plan override reason"
                    onChange={(e) => setPlanReason(e.target.value)}
                    placeholder="Reason (required)"
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    className={styles.expandBtn}
                    disabled={busy || plan === c.plan || planReason.trim() === ''}
                    onClick={() => setPlan(plan, planReason.trim())}
                  >
                    Apply plan
                  </button>
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 8 }}>
                  Manual changes are protected from Stripe reconciliation for seven days.
                  This does not create or cancel a subscription.
                </p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function OverrideNote({ label, entry, fallbackReason, context }: { label: string; entry?: OverrideAttribution; fallbackReason?: string | null; context?: string }) {
  return <div className={styles.overrideNote}>
    <strong>{label}</strong>
    {context && <small className={styles.overrideMeta}>{context}</small>}
    <span>{entry?.reason || fallbackReason || 'Reason unavailable'}</span>
    <small className={styles.overrideMeta}>
      {entry ? <>Latest matching audit: <span title={entry.actorId}>{entry.actorName || entry.actorId}</span> · {formatDate(entry.occurredAt)}</> : 'Audit attribution unavailable'}
    </small>
  </div>;
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  fontSize: 12,
  fontFamily: 'inherit',
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--surface)',
  color: 'inherit',
};

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return '-';
    return formatWhen(d);
  } catch {
    return '-';
  }
}
